// /opt/isp_erp/frontend/src/pages/Tickets.jsx
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  Grid,
  Card,
  CardContent,
  Tab,
  Tabs,
  TextField,
  InputAdornment,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Menu,
  Divider,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Badge,
  Stack,
  Alert,
  AlertTitle
} from '@mui/material';
import {
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Warning as WarningIcon,
  Home as HomeIcon,
  Delete as DeleteIcon,
  SwapHoriz as SwapHorizIcon,
  ArrowDropDown as ArrowDropDownIcon,
  Person as PersonIcon,
  FilterList as FilterListIcon,
  Event as EventIcon,
  Replay as ReplayIcon,
  Clear as ClearIcon,
  DateRange as DateRangeIcon,
  History as HistoryIcon,
  AccessTime as AccessTimeIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import { ticketsAPI } from '../api/tickets.api';
import { format, differenceInHours, differenceInDays } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/useAuth';
import TicketDetail from '../components/tickets/TicketDetail';
import CreateTicketDialog from '../components/tickets/CreateTicketDialog';
import PDFButton from '../components/tickets/PDFButton';
import TechnicianAvailabilityBadge from '../components/tickets/TechnicianAvailabilityBadge';
import ChangeAvailabilityDialog from '../components/tickets/ChangeAvailabilityDialog';
import ReassignTicketDialog from '../components/tickets/ReassignTicketDialog';
import RescheduleTicketDialog from '../components/tickets/RescheduleTicketDialog';
import ClienteBitacora from '../components/tickets/ClienteBitacora';

const priorityColors = {
  baja: 'success',
  media: 'info',
  alta: 'warning',
  critica: 'error'
};

const statusColors = {
  abierto: 'default',
  en_diagnostico: 'primary',
  asignado_campo: 'warning',
  resuelto: 'success',
  cerrado: 'secondary',
  cancelado: 'error'
};

const statusLabels = {
  abierto: 'Abierto',
  en_diagnostico: 'En Diagnóstico',
  asignado_campo: 'Asignado a Campo',
  resuelto: 'Resuelto',
  cerrado: 'Cerrado',
  cancelado: 'Cancelado'
};

export default function Tickets() {
  const navigate = useNavigate();

  // Obtener información del usuario y roles
  const { user, isTecnicoCampo, isAdmin, isSupervisor, hasRole } = useAuth();

  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPriority, setFilterPriority] = useState('');
  const [showMyTickets, setShowMyTickets] = useState(false);
  const [filters, setFilters] = useState({
    status: null,
    priority: null
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  // ✅ Estados para filtros de fecha
  const [fechaInicio, setFechaInicio] = useState(null);
  const [fechaFin, setFechaFin] = useState(null);

  // ✅ Estado para filtro por técnico
  const [filterTechnician, setFilterTechnician] = useState('');
  const [techniciansList, setTechniciansList] = useState([]);

  // Estados para los nuevos modales
  const [availabilityDialogOpen, setAvailabilityDialogOpen] = useState(false);
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [rescheduleDialogOpen, setRescheduleDialogOpen] = useState(false);
  const [selectedTechnician, setSelectedTechnician] = useState(null);

  // ✅ Estados para bitácora del cliente
  const [bitacoraClienteOpen, setBitacoraClienteOpen] = useState(false);
  const [bitacoraClienteData, setBitacoraClienteData] = useState(null);

  // ✅ Estado para alertas SLA
  const [slaAlerts, setSlaAlerts] = useState([]);
  const [slaLoading, setSlaLoading] = useState(false);

  // Verificar si el usuario tiene permisos para administrar tickets
  const canManageTickets = hasRole(['admin', 'supervisor', 'soporte_n1', 'soporte_n2']);

  // ✅ Solo Admin y Supervisor pueden reasignar y reagendar
  const canAssign = isAdmin() || isSupervisor();

  // ✅ Determinar el número de columnas de la tabla según el rol
  // Técnico de campo: sin columna "Fecha Creación" = 8 columnas
  // Otros roles: con columna "Fecha Creación" = 9 columnas
  const tableColumns = isTecnicoCampo() ? 8 : 9;

  // Si es técnico de campo, forzar que solo vea sus tickets
  useEffect(() => {
    if (isTecnicoCampo()) {
      setShowMyTickets(true);
    }
  }, [isTecnicoCampo]);

  // ✅ Cargar técnicos para el filtro
  useEffect(() => {
    loadTechniciansForFilter();
  }, []);

  // ✅ Cargar tickets cuando cambian filtros
  useEffect(() => {
    loadTickets();
    loadStats();
    loadSlaAlerts();
  }, [filters, showMyTickets, fechaInicio, fechaFin, filterTechnician]);

  const loadTechniciansForFilter = async () => {
    try {
      const data = await ticketsAPI.getTechnicians();
      setTechniciansList(data || []);
    } catch (error) {
      console.error('Error loading technicians for filter:', error);
    }
  };

  const loadTickets = async () => {
    setLoading(true);
    try {
      let params = { ...filters };

      if (isTecnicoCampo() || showMyTickets) {
        params.technician_id = user?.id;
        console.log('🔍 Filtrando tickets del técnico:', user?.id);
      }

      // ✅ Agregar filtro por técnico (solo si no es técnico de campo)
      if (!isTecnicoCampo() && filterTechnician) {
        params.technician_id = parseInt(filterTechnician);
      }

      // ✅ Agregar filtros de fecha
      if (fechaInicio) {
        params.fecha_inicio = fechaInicio.toISOString().split('T')[0];
      }
      if (fechaFin) {
        params.fecha_fin = fechaFin.toISOString().split('T')[0];
      }

      console.log('📋 Parámetros de búsqueda:', params);
      const data = await ticketsAPI.list(params);
      setTickets(data);
    } catch (error) {
      console.error('Error loading tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      let params = {};

      if (isTecnicoCampo()) {
        params.technician_id = user?.id;
        console.log('📊 Cargando estadísticas solo para técnico:', user?.id);
      }

      const data = await ticketsAPI.getStats(params);

      if (isTecnicoCampo() && data) {
        const myTickets = await ticketsAPI.list({ technician_id: user?.id });

        const myStats = {
          total: myTickets.length,
          open_tickets: myTickets.filter(t => t.status === 'abierto' || t.status === 'en_diagnostico' || t.status === 'asignado_campo').length,
          by_priority: {
            baja: myTickets.filter(t => t.priority === 'baja').length,
            media: myTickets.filter(t => t.priority === 'media').length,
            alta: myTickets.filter(t => t.priority === 'alta').length,
            critica: myTickets.filter(t => t.priority === 'critica').length
          }
        };
        setStats(myStats);
      } else {
        setStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
      if (isTecnicoCampo() && tickets.length > 0) {
        const myStats = {
          total: tickets.length,
          open_tickets: tickets.filter(t => t.status === 'abierto' || t.status === 'en_diagnostico' || t.status === 'asignado_campo').length,
          by_priority: {
            baja: tickets.filter(t => t.priority === 'baja').length,
            media: tickets.filter(t => t.priority === 'media').length,
            alta: tickets.filter(t => t.priority === 'alta').length,
            critica: tickets.filter(t => t.priority === 'critica').length
          }
        };
        setStats(myStats);
      }
    }
  };

  const calculateSlaAlerts = (ticketsList) => {
    if (!ticketsList || ticketsList.length === 0) {
      return [];
    }

    const alerts = [];
    const now = new Date();

    ticketsList.forEach(ticket => {
      if (ticket.status === 'cerrado' || ticket.status === 'cancelado') {
        return;
      }

      if (!ticket.created_at) {
        return;
      }

      const createdDate = new Date(ticket.created_at);
      const hoursSinceCreation = differenceInHours(now, createdDate);
      const daysSinceCreation = differenceInDays(now, createdDate);

      let slaHours = 0;
      let slaMessage = '';
      let severity = 'warning';

      switch (ticket.priority) {
        case 'critica':
          slaHours = 4;
          slaMessage = 'SLA crítico: Debe resolverse en 4 horas';
          severity = 'error';
          break;
        case 'alta':
          slaHours = 8;
          slaMessage = 'SLA alto: Debe resolverse en 8 horas';
          severity = 'error';
          break;
        case 'media':
          slaHours = 24;
          slaMessage = 'SLA medio: Debe resolverse en 24 horas';
          severity = 'warning';
          break;
        case 'baja':
          slaHours = 48;
          slaMessage = 'SLA bajo: Debe resolverse en 48 horas';
          severity = 'info';
          break;
        default:
          slaHours = 24;
          slaMessage = 'SLA estándar: Debe resolverse en 24 horas';
          severity = 'warning';
      }

      const hoursRemaining = slaHours - hoursSinceCreation;

      if (hoursRemaining <= 0) {
        alerts.push({
          ticket_id: ticket.id,
          ticket_number: ticket.ticket_number || `#${ticket.id}`,
          title: ticket.title || 'Sin título',
          priority: ticket.priority,
          status: ticket.status,
          message: `⛔ SLA VENCIDO - ${slaMessage}`,
          severity: 'error',
          hours_overdue: Math.abs(Math.round(hoursRemaining)),
          created_at: ticket.created_at
        });
      } else if (hoursRemaining <= 2) {
        alerts.push({
          ticket_id: ticket.id,
          ticket_number: ticket.ticket_number || `#${ticket.id}`,
          title: ticket.title || 'Sin título',
          priority: ticket.priority,
          status: ticket.status,
          message: `⚠️ SLA A PUNTO DE VENCER - ${Math.round(hoursRemaining)} horas restantes`,
          severity: 'warning',
          hours_remaining: Math.round(hoursRemaining),
          created_at: ticket.created_at
        });
      } else if (hoursRemaining <= 4 && ticket.priority === 'critica') {
        alerts.push({
          ticket_id: ticket.id,
          ticket_number: ticket.ticket_number || `#${ticket.id}`,
          title: ticket.title || 'Sin título',
          priority: ticket.priority,
          status: ticket.status,
          message: `🔴 SLA CRÍTICO - ${Math.round(hoursRemaining)} horas restantes`,
          severity: 'warning',
          hours_remaining: Math.round(hoursRemaining),
          created_at: ticket.created_at
        });
      }
    });

    alerts.sort((a, b) => {
      if (a.severity === 'error' && b.severity !== 'error') return -1;
      if (a.severity !== 'error' && b.severity === 'error') return 1;
      return (a.hours_overdue || 0) - (b.hours_overdue || 0);
    });

    return alerts;
  };

  const loadSlaAlerts = async () => {
    setSlaLoading(true);
    try {
      let params = {};

      if (isTecnicoCampo()) {
        params.technician_id = user?.id;
      } else if (filterTechnician) {
        params.technician_id = parseInt(filterTechnician);
      }

      if (fechaInicio) {
        params.fecha_inicio = fechaInicio.toISOString().split('T')[0];
      }
      if (fechaFin) {
        params.fecha_fin = fechaFin.toISOString().split('T')[0];
      }

      const ticketsData = await ticketsAPI.list(params);
      const calculatedAlerts = calculateSlaAlerts(ticketsData);
      setSlaAlerts(calculatedAlerts);
    } catch (error) {
      console.error('Error loading SLA alerts:', error);
      setSlaAlerts([]);
    } finally {
      setSlaLoading(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    const statusMap = {
      0: null,
      1: 'abierto',
      2: 'en_diagnostico',
      3: 'asignado_campo',
      4: 'resuelto',
      5: 'cerrado'
    };
    setFilters({ ...filters, status: statusMap[newValue] });
  };

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedTicket(null);
    loadTickets();
    loadStats();
    loadSlaAlerts();
  };

  const handleOpenClienteBitacora = (ticket) => {
    setBitacoraClienteData({
      customerId: ticket.customer_id,
      customerName: ticket.customer_name
    });
    setBitacoraClienteOpen(true);
  };

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleCreateTicket = (type) => {
    handleMenuClose();
    switch(type) {
      case 'incidente':
        setCreateDialogOpen(true);
        break;
      case 'instalacion':
        navigate('/tickets/instalacion');
        break;
      case 'retiro':
        navigate('/tickets/retiro');
        break;
      case 'cambio':
        navigate('/tickets/cambio-equipo');
        break;
      default:
        setCreateDialogOpen(true);
    }
  };

  const toggleMyTickets = () => {
    setShowMyTickets(!showMyTickets);
    setTabValue(0);
    setFilters({ ...filters, status: null });
  };

  const clearAllFilters = () => {
    setSearchTerm('');
    setFilterPriority('');
    setFechaInicio(null);
    setFechaFin(null);
    setFilterTechnician('');
    setTabValue(0);
    setFilters({ ...filters, status: null });
    setShowMyTickets(false);
  };

  const handleOpenAvailabilityDialog = (technicianId, technicianName) => {
    setSelectedTechnician({ id: technicianId, username: technicianName });
    setAvailabilityDialogOpen(true);
  };

  const handleOpenReassignDialog = (ticket) => {
    setSelectedTicket(ticket);
    setReassignDialogOpen(true);
  };

  const handleOpenRescheduleDialog = (ticket) => {
    setSelectedTicket(ticket);
    setRescheduleDialogOpen(true);
  };

  const showMyTicketsButton = !isTecnicoCampo() && (isAdmin() || isSupervisor());

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = searchTerm === '' ||
      ticket.ticket_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.customer_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = filterPriority === '' || ticket.priority === filterPriority;
    return matchesSearch && matchesPriority;
  });

  const renderSlaAlerts = () => {
    if (slaLoading) {
      return (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="body2" color="textSecondary">
            Cargando alertas SLA...
          </Typography>
        </Paper>
      );
    }

    if (slaAlerts.length === 0) {
      return (
        <Paper sx={{ p: 2, mb: 3, bgcolor: '#f5f5f5' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccessTimeIcon color="success" />
            <Typography variant="body2" color="textSecondary">
              {isTecnicoCampo() ? '✅ Todos tus tickets cumplen con los SLA' : '✅ No hay alertas SLA activas'}
            </Typography>
          </Box>
        </Paper>
      );
    }

    const errorAlerts = slaAlerts.filter(a => a.severity === 'error');
    const warningAlerts = slaAlerts.filter(a => a.severity === 'warning');
    const infoAlerts = slaAlerts.filter(a => a.severity === 'info');

    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <WarningIcon color="warning" />
          <Typography variant="subtitle1" fontWeight="bold">
            Alertas SLA
          </Typography>
          <Chip
            label={`${slaAlerts.length} alertas`}
            color={errorAlerts.length > 0 ? 'error' : 'warning'}
            size="small"
          />
          {isTecnicoCampo() && (
            <Chip
              label="Mis Tickets"
              color="primary"
              size="small"
              variant="outlined"
            />
          )}
        </Box>

        {errorAlerts.length > 0 && (
          <Alert severity="error" sx={{ mb: 1 }}>
            <AlertTitle>SLA VENCIDO</AlertTitle>
            {errorAlerts.map((alert, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                  label={alert.ticket_number}
                  size="small"
                  color="error"
                />
                <Typography variant="body2">
                  {alert.message} ({alert.hours_overdue} horas excedidas)
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {format(new Date(alert.created_at), 'dd/MM/yyyy HH:mm')}
                </Typography>
              </Box>
            ))}
          </Alert>
        )}

        {warningAlerts.length > 0 && (
          <Alert severity="warning" sx={{ mb: 1 }}>
            <AlertTitle>SLA PRÓXIMO A VENCER</AlertTitle>
            {warningAlerts.map((alert, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                  label={alert.ticket_number}
                  size="small"
                  color="warning"
                />
                <Typography variant="body2">
                  {alert.message}
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  {format(new Date(alert.created_at), 'dd/MM/yyyy HH:mm')}
                </Typography>
              </Box>
            ))}
          </Alert>
        )}

        {infoAlerts.length > 0 && (
          <Alert severity="info" sx={{ mb: 1 }}>
            <AlertTitle>Información SLA</AlertTitle>
            {infoAlerts.map((alert, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Chip
                  label={alert.ticket_number}
                  size="small"
                  color="info"
                />
                <Typography variant="body2">
                  {alert.message}
                </Typography>
              </Box>
            ))}
          </Alert>
        )}
      </Paper>
    );
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h4" component="h1">
            Sistema de Tickets
          </Typography>
          {showMyTickets && (
            <Chip
              icon={<PersonIcon />}
              label="Mis Tickets"
              color="primary"
              size="small"
            />
          )}
          {isTecnicoCampo() && (
            <Chip
              icon={<PersonIcon />}
              label="Técnico de Campo"
              color="info"
              size="small"
              variant="outlined"
            />
          )}
          {canManageTickets && (
            <Chip
              label="👑 Gestión"
              color="warning"
              size="small"
              variant="outlined"
            />
          )}
          {(fechaInicio || fechaFin) && (
            <Chip
              label="📅 Filtro por fecha activo"
              color="info"
              size="small"
              onDelete={() => {
                setFechaInicio(null);
                setFechaFin(null);
              }}
            />
          )}
        </Box>

        <Box>
          {showMyTicketsButton && (
            <Button
              variant={showMyTickets ? "contained" : "outlined"}
              startIcon={<PersonIcon />}
              onClick={toggleMyTickets}
              sx={{ mr: 1 }}
              color={showMyTickets ? "primary" : "inherit"}
            >
              {showMyTickets ? "Ver Todos" : "Mis Tickets"}
            </Button>
          )}

          {!isTecnicoCampo() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              endIcon={<ArrowDropDownIcon />}
              onClick={handleMenuClick}
            >
              Nuevo Ticket
            </Button>
          )}
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
          >
            <MenuItem onClick={() => handleCreateTicket('incidente')}>
              <ListItemIcon>
                <WarningIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Ticket de Incidente</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem onClick={() => handleCreateTicket('instalacion')}>
              <ListItemIcon>
                <HomeIcon fontSize="small" color="success" />
              </ListItemIcon>
              <ListItemText primary="Orden de Instalación" secondary="Instalar nuevo equipo" />
            </MenuItem>
            <MenuItem onClick={() => handleCreateTicket('retiro')}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText primary="Orden de Retiro" secondary="Retirar equipo existente" />
            </MenuItem>
            <MenuItem onClick={() => handleCreateTicket('cambio')}>
              <ListItemIcon>
                <SwapHorizIcon fontSize="small" color="warning" />
              </ListItemIcon>
              <ListItemText primary="Orden de Cambio" secondary="Reemplazar equipo" />
            </MenuItem>
          </Menu>
        </Box>
      </Box>

      {renderSlaAlerts()}

      {/* Estadísticas */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {isTecnicoCampo() ? 'Mis Tickets' : 'Total Tickets'}
                </Typography>
                <Typography variant="h4">
                  {stats.total || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {isTecnicoCampo() ? 'Mis Tickets Abiertos' : 'Tickets Abiertos'}
                </Typography>
                <Typography variant="h4" color="primary">
                  {stats.open_tickets || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {isTecnicoCampo() ? 'Mis Tickets Alta Prioridad' : 'Alta Prioridad'}
                </Typography>
                <Typography variant="h4" color="warning">
                  {stats.by_priority?.alta || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="textSecondary" gutterBottom>
                  {isTecnicoCampo() ? 'Mis Tickets Críticos' : 'Críticos'}
                </Typography>
                <Typography variant="h4" color="error">
                  {stats.by_priority?.critica || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* ✅ Filtros con fechas y técnico */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              placeholder="Buscar..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <FormControl fullWidth size="small">
              <InputLabel>Prioridad</InputLabel>
              <Select
                value={filterPriority}
                onChange={(e) => setFilterPriority(e.target.value)}
                label="Prioridad"
              >
                <MenuItem value="">Todas</MenuItem>
                <MenuItem value="baja">Baja</MenuItem>
                <MenuItem value="media">Media</MenuItem>
                <MenuItem value="alta">Alta</MenuItem>
                <MenuItem value="critica">Crítica</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {!isTecnicoCampo() && (
            <Grid item xs={12} sm={6} md={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Técnico</InputLabel>
                <Select
                  value={filterTechnician}
                  onChange={(e) => setFilterTechnician(e.target.value)}
                  label="Técnico"
                >
                  <MenuItem value="">Todos</MenuItem>
                  {techniciansList.map((tech) => (
                    <MenuItem key={tech.id} value={tech.id}>
                      {tech.full_name || tech.username}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha Inicio"
                value={fechaInicio}
                onChange={setFechaInicio}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
              <DatePicker
                label="Fecha Fin"
                value={fechaFin}
                onChange={setFechaFin}
                slotProps={{
                  textField: {
                    fullWidth: true,
                    size: 'small'
                  }
                }}
              />
            </LocalizationProvider>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                onClick={loadTickets}
                startIcon={<SearchIcon />}
                size="small"
                fullWidth
              >
                Buscar
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6} md={1.5}>
            <Button
              variant="outlined"
              onClick={clearAllFilters}
              startIcon={<ClearIcon />}
              size="small"
              color="secondary"
              fullWidth
            >
              Limpiar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Tabs de estado */}
      <Paper sx={{ mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          <Tab label="Todos" />
          <Tab label="Abiertos" />
          <Tab label="En Diagnóstico" />
          <Tab label="En Campo" />
          <Tab label="Resueltos" />
          <Tab label="Cerrados" />
        </Tabs>
      </Paper>

      {/* Tabla de tickets */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Ticket</TableCell>
              <TableCell>Cliente</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Prioridad</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Técnico</TableCell>
              {/* ✅ Fecha Creación - solo visible para admin/supervisor/soporte */}
              {!isTecnicoCampo() && <TableCell>Fecha Creación</TableCell>}
              <TableCell>Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={tableColumns} align="center">Cargando...</TableCell>
              </TableRow>
            ) : filteredTickets.length === 0 ? (
              <TableRow>
                <TableCell colSpan={tableColumns} align="center">
                  {showMyTickets ? 'No tienes tickets asignados' : 'No hay tickets'}
                </TableCell>
              </TableRow>
            ) : (
              filteredTickets.map((ticket) => (
                <TableRow key={ticket.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">
                      {ticket.ticket_number}
                    </Typography>
                  </TableCell>
                  <TableCell>{ticket.customer_name}</TableCell>
                  <TableCell>{ticket.title}</TableCell>
                  <TableCell>
                    <Chip
                      label={(ticket.ticket_type || '').toLowerCase() === 'instalacion' ? '📦 Instalación' :
                             (ticket.ticket_type || '').toLowerCase() === 'retiro' ? '📤 Retiro' :
                             (ticket.ticket_type || '').toLowerCase() === 'cambio_equipo' ? '🔄 Cambio' : '⚠️ Incidente'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={ticket.priority}
                      color={priorityColors[ticket.priority]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabels[ticket.status]}
                      color={statusColors[ticket.status]}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {ticket.assigned_technician_id ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <TechnicianAvailabilityBadge
                          technicianId={ticket.assigned_technician_id}
                          technicianName={ticket.assigned_technician_name}
                        />
                      </Box>
                    ) : (
                      'Sin asignar'
                    )}
                  </TableCell>
                  {/* ✅ Fecha Creación - solo visible para admin/supervisor/soporte */}
                  {!isTecnicoCampo() && (
                    <TableCell>
                      {format(new Date(ticket.created_at), 'dd/MM/yyyy HH:mm', { locale: es })}
                    </TableCell>
                  )}
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                      <Tooltip title="Ver detalle">
                        <IconButton
                          size="small"
                          onClick={() => handleViewTicket(ticket)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>

                      {ticket.customer_id && (
                        <Tooltip title="Ver bitácora del cliente">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleOpenClienteBitacora(ticket)}
                          >
                            <HistoryIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}

                      {canAssign && ticket.assigned_technician_id && (
                        <Tooltip title="Reasignar ticket a otro técnico">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={() => handleOpenReassignDialog(ticket)}
                          >
                            <SwapHorizIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {canAssign && (
                        <Tooltip title="Reagendar ticket">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleOpenRescheduleDialog(ticket)}
                          >
                            <EventIcon />
                          </IconButton>
                        </Tooltip>
                      )}

                      {ticket.ticket_type === 'instalacion' && (
                        <PDFButton
                          ticketId={ticket.id}
                          ticketNumber={ticket.ticket_number}
                          variant="icon"
                        />
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog de detalle */}
      <TicketDetail
        open={dialogOpen}
        onClose={handleCloseDialog}
        ticket={selectedTicket}
        onUpdate={loadTickets}
      />

      {/* Dialog de creación (para incidentes) */}
      <CreateTicketDialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        onSuccess={() => {
          setCreateDialogOpen(false);
          loadTickets();
          loadStats();
          loadSlaAlerts();
        }}
      />

      {/* Dialog de cambio de disponibilidad */}
      {selectedTechnician && (
        <ChangeAvailabilityDialog
          open={availabilityDialogOpen}
          onClose={() => {
            setAvailabilityDialogOpen(false);
            setSelectedTechnician(null);
          }}
          technician={selectedTechnician}
          onSuccess={() => {
            loadTickets();
            loadStats();
            loadSlaAlerts();
          }}
        />
      )}

      {/* Dialog de reasignación */}
      <ReassignTicketDialog
        open={reassignDialogOpen}
        onClose={() => {
          setReassignDialogOpen(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
        fechaTicket={selectedTicket?.arrival_time || selectedTicket?.scheduled_date}
        onSuccess={() => {
          loadTickets();
          loadStats();
          loadSlaAlerts();
        }}
      />

      {/* Dialog de reagendamiento */}
      <RescheduleTicketDialog
        open={rescheduleDialogOpen}
        onClose={() => {
          setRescheduleDialogOpen(false);
          setSelectedTicket(null);
        }}
        ticket={selectedTicket}
        onSuccess={() => {
          loadTickets();
          loadStats();
          loadSlaAlerts();
        }}
      />

      {/* Dialog de bitácora del cliente */}
      <ClienteBitacora
        open={bitacoraClienteOpen}
        onClose={() => {
          setBitacoraClienteOpen(false);
          setBitacoraClienteData(null);
        }}
        customerId={bitacoraClienteData?.customerId}
        customerName={bitacoraClienteData?.customerName}
      />
    </Box>
  );
}
